'use client'
import React, { useState, useEffect, useRef } from 'react'
import { useRouter } from 'next/navigation'
import { updateSearchParams, removeParams, removeSearchParam } from '@utils'
import { FaMapMarkerAlt, FaList } from 'react-icons/fa'
import { AiOutlineMenuFold } from 'react-icons/ai'
import { categoryTypes } from '@constants/homeCategoryTypes'
import { homeRoomTypes } from '@constants/homeRoomTypes'
import { saleMaxPrices } from '@constants/saleMaxPrice'
import { saleMinPrices } from '@constants/saleMinPrice'
import { rentMaxPrices } from '@constants/rentMaxPrice'
import { rentMinPrices } from '@constants/rentMinPrice'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const SLOT_LABEL_DEFAULTS = {
  saleType:'Sale Type', category:'Category', categories:'Categories',
  district:'District', districts:'Districts', bts:'BTS / MRT',
  bedrooms:'Bedrooms', bathrooms:'Bathrooms', minPrice:'Min Price',
  maxPrice:'Max Price', minRentPrice:'Min Rent', maxRentPrice:'Max Rent',
  sort:'Sort By', nameSearch:'Name / Ref', petFriendly:'Pet Friendly',
  seaView:'Sea View', beachfront:'Beachfront', pool:'Pool',
  gym:'Gym', parking:'Parking', furnished:'Furnished',
  searchButton:'Search', resetButton:'Reset', mapToggle:'Map View',
}

function resolveLabel(slot, slotLabels, lang) {
  return slotLabels?.[slot]?.[lang] || slotLabels?.[slot]?.en || SLOT_LABEL_DEFAULTS[slot] || slot
}

function getParam(key) {
  if (typeof window === 'undefined') return null
  return new URLSearchParams(window.location.search).get(key)
}

// ─── Shared dropdown style builder ───────────────────────────────────────────

function buildDropdownStyle(fs) {
  return {
    width: '100%',
    height: fs.height || '38px',
    background: fs.bg || '#fff',
    color: fs.textColor || '#111',
    border: `${fs.borderWidth || 1}px solid ${fs.borderColor || '#d1d5db'}`,
    borderRadius: fs.borderRadius || '6px',
    fontSize: fs.fontSize || '0.875rem',
    padding: '0 10px',
    outline: 'none',
    cursor: 'pointer',
    boxSizing: 'border-box',
    appearance: 'none',
    WebkitAppearance: 'none',
  }
}

// ─── Dropdown wrapper with chevron ────────────────────────────────────────────

function DropdownWrap({ fs, children }) {
  return (
    <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
      {children}
      <span style={{
        position: 'absolute', right: 10, top: '50%',
        transform: 'translateY(-50%)',
        pointerEvents: 'none', fontSize: '0.65em',
        color: fs.textColor || '#111', opacity: 0.5,
      }}>▾</span>
    </div>
  )
}

// ─── Field label ──────────────────────────────────────────────────────────────

function FieldLabel({ label, fs }) {
  if (!fs.showLabels) return null
  return (
    <span style={{
      display: 'block',
      fontSize: fs.labelSize || '0.75rem',
      fontWeight: fs.labelWeight || '600',
      color: fs.labelColor || '#374151',
      marginBottom: 4,
    }}>
      {label}
    </span>
  )
}

// ─── Generic select ───────────────────────────────────────────────────────────

function GenericSelect({ paramKey, options, label, fs, router, placeholder }) {
  const [value, setValue] = useState(() => getParam(paramKey) || '')

  useEffect(() => { setValue(getParam(paramKey) || '') }, [paramKey])

  const handleChange = (e) => {
    const v = e.target.value
    setValue(v)
    const path = v ? updateSearchParams(paramKey, v) : removeSearchParam(paramKey)
    router.push(path)
    router.refresh()
  }

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <FieldLabel label={label} fs={fs} />
      <DropdownWrap fs={fs}>
        <select value={value} onChange={handleChange} style={buildDropdownStyle(fs)}>
          <option value="">{placeholder || label}</option>
          {options.map(o => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </DropdownWrap>
    </div>
  )
}

// ─── Custom dropdown (searchable list) ───────────────────────────────────────

function SearchableSelect({ paramKey, options, label, fs, router, placeholder }) {
  const [value, setValue]   = useState(() => getParam(paramKey) || '')
  const [open, setOpen]     = useState(false)
  const [query, setQuery]   = useState('')
  const ref = useRef(null)

  useEffect(() => { setValue(getParam(paramKey) || '') }, [paramKey])

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

    const filtered = options.filter(o => {
    const l = o.label != null ? String(o.label) : ''
    return l.toLowerCase().includes(query.toLowerCase())
    })
  const selected = options.find(o => o.value === value)

  const select = (o) => {
    setValue(o.value)
    setOpen(false)
    setQuery('')
    const path = updateSearchParams(paramKey, o.value)
    router.push(path)
    router.refresh()
  }

  const clear = () => {
    setValue('')
    setOpen(false)
    setQuery('')
    router.push(removeSearchParam(paramKey))
    router.refresh()
  }

  const fs_menu = { background: fs.menuBg || '#fff', color: fs.menuTextColor || '#374' }

  return (
    <div ref={ref} style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <FieldLabel label={label} fs={fs} />

      {/* Trigger */}
      <div
        onClick={() => setOpen(o => !o)}
        style={{
          ...buildDropdownStyle(fs),
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: fs.height || '38px', cursor: 'pointer', userSelect: 'none',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {selected ? selected.label : <span style={{ opacity: 0.5 }}>{placeholder || label}</span>}
        </span>
        <span style={{ display: 'flex', gap: 4, alignItems: 'center', flexShrink: 0 }}>
          {value && (
            <span
              onClick={(e) => { e.stopPropagation(); clear() }}
              style={{ opacity: 0.4, fontSize: '0.8em', padding: '0 2px', cursor: 'pointer' }}
            >✕</span>
          )}
          <span style={{ opacity: 0.5, fontSize: '0.65em' }}>▾</span>
        </span>
      </div>

      {/* Menu */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: fs_menu.background,
          border: `1px solid ${fs.borderColor || '#d1d5db'}`,
          borderRadius: fs.borderRadius || '6px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 200, maxHeight: 260, display: 'flex', flexDirection: 'column',
        }}>
          <div style={{ padding: '6px 8px', borderBottom: `1px solid ${fs.borderColor || '#e5e7eb'}` }}>
            <input
              autoFocus
              value={query}
              onChange={e => setQuery(e.target.value)}
              placeholder="Search…"
              style={{
                width: '100%', border: 'none', outline: 'none',
                fontSize: fs.fontSize || '0.875rem',
                background: 'transparent', color: fs_menu.color,
                boxSizing: 'border-box',
              }}
            />
          </div>
          <div style={{ overflowY: 'auto', maxHeight: 200 }}>
            {filtered.length === 0 && (
              <div style={{ padding: '8px 12px', color: '#aaa', fontSize: '0.8rem' }}>No results</div>
            )}
            {filtered.map(o => {
              const isSelected = o.value === value
              return (
                <div
                  key={o.value}
                  onClick={() => select(o)}
                  style={{
                    padding: '8px 12px',
                    cursor: 'pointer',
                    fontSize: fs.fontSize || '0.875rem',
                    background: isSelected ? (fs.selectedBg || '#eff6ff') : 'transparent',
                    color: isSelected ? (fs.selectedTextColor || '#1d4ed8') : fs_menu.color,
                    fontWeight: isSelected ? (fs.selectedFontWeight || '600') : 'normal',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                  }}
                  onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = fs.hoverBg || '#f3f4f6' }}
                  onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
                >
                  {o.label}
                  {isSelected && (fs.selectedShowCheck ?? true) && <span style={{ fontSize: '0.8em' }}>✓</span>}
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Multi-select dropdown ────────────────────────────────────────────────────

function MultiSelect({ paramKey, options, label, fs, router, placeholder }) {
  const [values, setValues] = useState(() => {
    const v = getParam(paramKey)
    return v ? v.split(',') : []
  })
  const [open, setOpen] = useState(false)
  const ref = useRef(null)

  useEffect(() => {
    const v = getParam(paramKey)
    setValues(v ? v.split(',') : [])
  }, [paramKey])

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false) }
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [])

  const toggle = (val) => {
    const next = values.includes(val) ? values.filter(v => v !== val) : [...values, val]
    setValues(next)
    const path = next.length ? updateSearchParams(paramKey, next.join(',')) : removeSearchParam(paramKey)
    router.push(path)
    router.refresh()
  }

  const displayLabel = values.length
    ? options.filter(o => values.includes(o.value)).map(o => o.label).join(', ')
    : null

  return (
    <div ref={ref} style={{ flex: 1, minWidth: 0, position: 'relative' }}>
      <FieldLabel label={label} fs={fs} />

      <div
        onClick={() => setOpen(o => !o)}
        style={{
          ...buildDropdownStyle(fs),
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          height: fs.height || '38px', cursor: 'pointer', userSelect: 'none',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayLabel
            ? <span>{displayLabel}</span>
            : <span style={{ opacity: 0.5 }}>{placeholder || label}</span>
          }
        </span>
        <span style={{ opacity: 0.5, fontSize: '0.65em', flexShrink: 0 }}>▾</span>
      </div>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
          background: fs.menuBg || '#fff',
          border: `1px solid ${fs.borderColor || '#d1d5db'}`,
          borderRadius: fs.borderRadius || '6px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
          zIndex: 200, maxHeight: 260, overflowY: 'auto',
        }}>
          {options.map(o => {
            const isSelected = values.includes(o.value)
            return (
              <div
                key={o.value}
                onClick={() => toggle(o.value)}
                style={{
                  padding: '8px 12px',
                  cursor: 'pointer',
                  fontSize: fs.fontSize || '0.875rem',
                  background: isSelected ? (fs.selectedBg || '#eff6ff') : 'transparent',
                  color: isSelected ? (fs.selectedTextColor || '#1d4ed8') : (fs.menuTextColor || '#374'),
                  fontWeight: isSelected ? (fs.selectedFontWeight || '600') : 'normal',
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                }}
                onMouseEnter={e => { if (!isSelected) e.currentTarget.style.background = fs.hoverBg || '#f3f4f6' }}
                onMouseLeave={e => { if (!isSelected) e.currentTarget.style.background = 'transparent' }}
              >
                {o.label}
                {isSelected && (fs.selectedShowCheck ?? true) && <span style={{ fontSize: '0.8em' }}>✓</span>}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ─── Toggle switch ────────────────────────────────────────────────────────────

function ToggleSwitch({ paramKey, label, sw, router }) {
  const [on, setOn] = useState(() => getParam(paramKey) === 'true')

  useEffect(() => { setOn(getParam(paramKey) === 'true') }, [paramKey])

  const toggle = () => {
    const next = !on
    setOn(next)
    const path = next ? updateSearchParams(paramKey, 'true') : removeSearchParam(paramKey)
    router.push(path)
    router.refresh()
  }

  const isLeft = sw.layout === 'label-left'
  const isTop  = sw.layout === 'label-top'

  return (
    <div onClick={toggle} style={{
      display: 'flex',
      flexDirection: isTop ? 'column' : isLeft ? 'row-reverse' : 'row',
      alignItems: isTop ? 'flex-start' : 'center',
      gap: 6, cursor: 'pointer', userSelect: 'none',
    }}>
      <div style={{
        width: 36, height: 20, borderRadius: 10,
        background: on ? (sw.activeColor || '#3b82f6') : '#d1d5db',
        position: 'relative', flexShrink: 0, transition: 'background 0.2s',
      }}>
        <div style={{
          position: 'absolute', top: 2, left: on ? 18 : 2,
          width: 16, height: 16, borderRadius: '50%', background: '#fff',
          transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
        }} />
      </div>
      <span style={{
        fontSize: sw.labelSize || '0.875rem',
        fontWeight: sw.labelWeight || 'normal',
        color: sw.labelColor || '#374151',
      }}>
        {label}
      </span>
    </div>
  )
}

// ─── Price input ──────────────────────────────────────────────────────────────

function PriceInput({ paramKey, label, fs, router, placeholder }) {
  const [value, setValue] = useState(() => getParam(paramKey) || '')

  useEffect(() => { setValue(getParam(paramKey) || '') }, [paramKey])

  const handleBlur = () => {
    const path = value ? updateSearchParams(paramKey, value) : removeSearchParam(paramKey)
    router.push(path)
    router.refresh()
  }

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <FieldLabel label={label} fs={fs} />
      <input
        type="number"
        value={value}
        onChange={e => setValue(e.target.value)}
        onBlur={handleBlur}
        placeholder={placeholder || label}
        style={{ ...buildDropdownStyle(fs), appearance: 'none' }}
      />
    </div>
  )
}

// ─── Name search ──────────────────────────────────────────────────────────────

function NameSearchInput({ label, fs, router }) {
  const [value, setValue] = useState(() => getParam('name') || '')

  const handleChange = (e) => {
    const v = e.target.value
    setValue(v)
    const path = v ? updateSearchParams('name', v) : removeSearchParam('name')
    router.push(path)
  }

  return (
    <div style={{ flex: 1, minWidth: 0 }}>
      <FieldLabel label={label} fs={fs} />
      <input
        type="text"
        value={value}
        onChange={handleChange}
        placeholder={label}
        style={buildDropdownStyle(fs)}
      />
    </div>
  )
}

// ─── Slot renderer ────────────────────────────────────────────────────────────

function SlotRenderer({ slot, config, lang, btsList, districtList, showMap, onMapToggle, onSearch, onReset, salePriceFilter, categoryFilter, router }) {
  const fs = config.filterStyle || {}
  const sw = config.switchStyle || {}
  const sb = config.searchBtn   || {}
  const rb = config.resetBtn    || {}
  const sl = config.slotLabels  || {}
  const label = resolveLabel(slot, sl, lang)

  // ── Sale Type ──
  if (slot === 'saleType') {
    const options = (config.saleTypeOptions || [
      { value: 'sale', label: 'Sale' },
      { value: 'rent', label: 'Rent' },
    ]).map(o => ({
      value: o.value,
      label: sl?.saleType?.[lang] ? o.label : o.label, // per-type labels could be added later
    }))
    return <GenericSelect paramKey="saleType" options={options} label={label} fs={fs} router={router} />
  }

  // ── Category (single) ──
  if (slot === 'category') {
    const options = (categoryTypes?.[lang] || []).map(o => ({
      value: String(o.value),
      label: String(o.title || o.label || o.value),
    }))
    return <SearchableSelect paramKey="category" options={options} label={label} fs={fs} router={router} />
  }

  // ── Categories (multi) ──
  if (slot === 'categories') {
    const options = (categoryTypes?.[lang] || [])
      .filter(o => o.value !== 'all')
      .map(o => ({
        value: String(o.value),
        label: String(o.title || o.label || o.value),
      }))
    return <MultiSelect paramKey="category" options={options} label={label} fs={fs} router={router} />
  }

    // ── District (single) ──
    if (slot === 'district') {
    const options = (districtList || []).map(d => ({
        value: String(d.value || d.id || d),
        label: String(d.label || d.subdistrict || d),
    }))
    return <SearchableSelect paramKey="district" options={options} label={label} fs={fs} router={router} />
    }

    // ── Districts (multi) ──
    if (slot === 'districts') {
    const options = (districtList || []).map(d => ({
        value: String(d.value || d.id || d),
        label: String(d.label || d.subdistrict || d),
    }))
    return <MultiSelect paramKey="district" options={options} label={label} fs={fs} router={router} />
    }

    // ── BTS / MRT ──
    if (slot === 'bts') {
    const options = (btsList || []).map(b => ({
        value: String(b.id),
        label: String(b.stationName),
    }))
    return <SearchableSelect paramKey="bts" options={options} label={label} fs={fs} router={router} />
    }

  // ── Bedrooms ──
  if (slot === 'bedrooms') {
    const options = (homeRoomTypes?.[lang] || []).map(o => ({
      value: String(o.value),
      label: String(o.title || o.label || o.value),
    }))
    return <GenericSelect paramKey="room" options={options} label={label} fs={fs} router={router} placeholder={label} />
  }


  // ── Bathrooms ──
  if (slot === 'bathrooms') {
    const options = [
      { value: '1', label: '1' }, { value: '2', label: '2' },
      { value: '3', label: '3' }, { value: '4', label: '4+' },
    ]
    return <GenericSelect paramKey="bathrooms" options={options} label={label} fs={fs} router={router} placeholder={label} />
  }

  // ── Min Price ──
if (slot === 'minPrice') {
  const rentMode = salePriceFilter === 'rent'
  const options = ((rentMode ? rentMinPrices : saleMinPrices)?.[lang] || []).map(o => ({
    value: String(o.value),
    label: String(o.nom || o.label || o.value),
  }))
  return <GenericSelect paramKey="minprice" options={options} label={label} fs={fs} router={router} placeholder={label} />
}

// ── Max Price ──
if (slot === 'maxPrice') {
  const rentMode = salePriceFilter === 'rent'
  const options = ((rentMode ? rentMaxPrices : saleMaxPrices)?.[lang] || []).map(o => ({
    value: String(o.value),
    label: String(o.nom || o.label || o.value),
  }))
  return <GenericSelect paramKey="maxprice" options={options} label={label} fs={fs} router={router} placeholder={label} />
}

// ── Min Rent Price (explicit slot) ──
if (slot === 'minRentPrice') {
  const options = (rentMinPrices?.[lang] || []).map(o => ({
    value: String(o.value),
    label: String(o.nom || o.label || o.value),
  }))
  return <GenericSelect paramKey="minprice" options={options} label={label} fs={fs} router={router} placeholder={label} />
}

// ── Max Rent Price (explicit slot) ──
if (slot === 'maxRentPrice') {
  const options = (rentMaxPrices?.[lang] || []).map(o => ({
    value: String(o.value),
    label: String(o.nom || o.label || o.value),
  }))
  return <GenericSelect paramKey="maxprice" options={options} label={label} fs={fs} router={router} placeholder={label} />
}

  // ── Sort ──
  if (slot === 'sort') {
    const options = [
      { value: 'datedesc',  label: 'Newest First' },
      { value: 'dateasc',   label: 'Oldest First' },
      { value: 'priceasc',  label: 'Price: Low → High' },
      { value: 'pricedesc', label: 'Price: High → Low' },
    ]
    return <GenericSelect paramKey="sort" options={options} label={label} fs={fs} router={router} />
  }

  // ── Name search ──
  if (slot === 'nameSearch') {
    return <NameSearchInput label={label} fs={fs} router={router} />
  }

  // ── Switches ──
  if (slot === 'petFriendly')  return <ToggleSwitch paramKey="petfriendly"  label={label} sw={sw} router={router} />
  if (slot === 'seaView')      return <ToggleSwitch paramKey="seaview"      label={label} sw={sw} router={router} />
  if (slot === 'beachfront')   return <ToggleSwitch paramKey="beachfront"   label={label} sw={sw} router={router} />
  if (slot === 'pool')         return <ToggleSwitch paramKey="pool"         label={label} sw={sw} router={router} />
  if (slot === 'gym')          return <ToggleSwitch paramKey="gym"          label={label} sw={sw} router={router} />
  if (slot === 'parking')      return <ToggleSwitch paramKey="parking"      label={label} sw={sw} router={router} />
  if (slot === 'furnished')    return <ToggleSwitch paramKey="furnished"    label={label} sw={sw} router={router} />

  // ── Search button ──
  if (slot === 'searchButton') {
    const btnLabel = sb.label?.[lang] || sb.label?.en || 'Search'
    return (
      <button onClick={onSearch} style={{
        background: sb.bg || '#111', color: sb.textColor || '#fff',
        borderRadius: sb.borderRadius || '6px',
        height: fs.height || '38px', padding: '0 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 6, fontWeight: 600, fontSize: fs.fontSize || '0.875rem',
        cursor: 'pointer', border: 'none',
        flex: sb.fullWidth ? 1 : undefined, whiteSpace: 'nowrap',
      }}>
        {(sb.showIcon  ?? true)  && '🔍'}
        {(sb.showLabel ?? true)  && btnLabel}
      </button>
    )
  }

  // ── Reset button ──
  if (slot === 'resetButton') {
    const btnLabel = rb.label?.[lang] || rb.label?.en || 'Reset'
    return (
      <button onClick={onReset} style={{
        background: rb.bg || '#ef4444', color: rb.textColor || '#fff',
        borderRadius: rb.borderRadius || '6px',
        height: fs.height || '38px', padding: '0 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        gap: 6, fontWeight: 600, fontSize: fs.fontSize || '0.875rem',
        cursor: 'pointer', border: 'none',
        flex: rb.fullWidth ? 1 : undefined, whiteSpace: 'nowrap',
      }}>
        {(rb.showIcon  ?? false) && '↺'}
        {(rb.showLabel ?? true)  && btnLabel}
      </button>
    )
  }

  // ── Map toggle ──
  if (slot === 'mapToggle') {
    return (
      <button onClick={() => onMapToggle(!showMap)} style={{
        background: fs.bg || '#fff', color: fs.textColor || '#111',
        border: `${fs.borderWidth || 1}px solid ${fs.borderColor || '#d1d5db'}`,
        borderRadius: fs.borderRadius || '6px',
        height: fs.height || '38px', padding: '0 12px',
        display: 'flex', alignItems: 'center', gap: 6,
        fontSize: fs.fontSize || '0.875rem',
        cursor: 'pointer', whiteSpace: 'nowrap',
      }}>
        {showMap ? <FaList size={14} /> : <FaMapMarkerAlt size={14} />}
        {showMap
          ? (resolveLabel('listView', sl, lang) || 'List')
          : label
        }
      </button>
    )
  }

  return null
}

// ─── Main component ───────────────────────────────────────────────────────────

const PanelSearch = ({ searchFilter, searchParams, btsList, districtList, lang, dictionary }) => {
  // console.log(btsList)
  // console.log(districtList)
  const router = useRouter()
  const filterRef = useRef(null)
  const config = searchFilter || {}
  const columns = config.columns || []

  const [salePriceFilter, setSalePriceFilter] = useState(searchParams?.saleType || 'sale')
  const [categoryFilter,  setCategoryFilter]  = useState(searchParams?.category)
  const [showMap,         setShowMap]         = useState(searchParams?.map === 'true' || searchParams?.map === true)
  const [filterOn,        setFilterOn]        = useState(false)
  const [isOpen,          setIsOpen]          = useState(config.displayMode !== 'button-triggered')

  useEffect(() => {
    const sp = searchParams || {}
    setSalePriceFilter(sp.saleType || 'sale')
    setCategoryFilter(sp.category)
    setShowMap(sp.map === 'true' || sp.map === true)
    setFilterOn(
      sp.category  !== undefined ||
      sp.room      !== undefined ||
      sp.minprice  !== undefined ||
      sp.maxprice  !== undefined ||
      sp.district  !== undefined ||
      sp.bts       !== undefined
    )
    if (config.displayMode !== 'button-triggered') setIsOpen(true)
  }, [searchParams])

  useEffect(() => {
    if (searchParams?.filtertab === 'true') setIsOpen(true)
  }, [])

  // Sticky
  useEffect(() => {
    if (!filterRef.current) return
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && filterRef.current) {
        filterRef.current.style.position = 'sticky'
        filterRef.current.style.top = '0'
      }
    }, { root: null, threshold: 0 })
    observer.observe(filterRef.current)
    return () => observer.disconnect()
  }, [])

  const handleMapToggle = (next) => {
    setShowMap(next)
    router.push(next ? updateSearchParams('map', 'true') : removeSearchParam('map'))
    router.refresh()
  }

  const handleSearch = () => {
    setIsOpen(false)
    router.refresh()
  }

  const handleReset = () => removeParams()

  const slotProps = {
    config, lang, btsList, districtList, searchParams,
    showMap, salePriceFilter, categoryFilter,
    onMapToggle: handleMapToggle,
    onSearch: handleSearch,
    onReset: handleReset,
    router,
  }

  const isButtonTriggered = config.displayMode === 'button-triggered'
  const triggerLabel = config.triggerLabel?.[lang] || config.triggerLabel?.en || 'Filter'

  const panelInner = (
    <div style={{
      background:   config.panelBg          || '#ffffff',
      border:       `1px solid ${config.panelBorderColor || '#e5e7eb'}`,
      borderRadius: config.panelBorderRadius || '8px',
      boxShadow:    config.panelBoxShadow    || 'none',
      padding:      `${config.panelPaddingY || 16}px ${config.panelPaddingX || 16}px`,
      display:      'flex',
      gap:          `${config.columnGap || 16}px`,
      flexWrap:     'wrap',
      alignItems:   'flex-start',
    }}>
      {columns.map((col, ci) => (
        <div key={col.id || ci} style={{
          display:       'flex',
          flexDirection: 'column',
          gap:           `${config.rowGap || 8}px`,
          alignItems:    col.justify || 'flex-start',
          flex:          col.widthPercent ? `0 0 ${col.widthPercent}%` : '1',
          minWidth:      0,
        }}>
          {(col.rows || []).map((row, ri) => (
            <div key={row.id || ri} style={{
              display:    'flex',
              gap:        `${row.gap || 8}px`,
              flexWrap:   row.wrap ? 'wrap' : 'nowrap',
              alignItems: row.align || 'center',
              width:      '100%',
            }}>
              {(row.slots || []).map((slot, si) => (
                <SlotRenderer key={`${slot}-${si}`} slot={slot} {...slotProps} />
              ))}
            </div>
          ))}
        </div>
      ))}
    </div>
  )

  if (!isButtonTriggered) {
    return (
      <div className='container-wide mx-auto' ref={filterRef} style={{ zIndex: 90, position: 'relative' }}>
        {panelInner}
      </div>
    )
  }

  return (
    <div className='container-wide mx-auto' ref={filterRef} style={{zIndex: 90, position: 'relative' }}>

      {/* Trigger bar */}
<div style={{
  display: 'flex', justifyContent: 'flex-end', alignItems: 'center',
  padding: '6px 12px',
  borderBottom: `1px solid ${config.panelBorderColor || '#e5e7eb'}`,
  background: config.panelBg || '#fff',
  gap: '6px'
}}>
  {/* Map toggle — left side */}
  <button
    onClick={() => handleMapToggle(!showMap)}
    style={{
      background: config.filterStyle?.bg || '#fff',
      color: config.filterStyle?.textColor || '#111',
      border: `1px solid ${config.filterStyle?.borderColor || '#d1d5db'}`,
      borderRadius: config.filterStyle?.borderRadius || '6px',
      padding: '6px 12px',
      display: 'flex', alignItems: 'center', gap: 6,
      cursor: 'pointer', fontWeight: 600, fontSize: '0.875rem',
    }}
  >
    {showMap ? <FaList size={14} /> : <FaMapMarkerAlt size={14} />}
    {showMap
      ? (resolveLabel('listView', config.slotLabels, lang) || 'List')
      : resolveLabel('mapToggle', config.slotLabels, lang)
    }
  </button>

  {/* Filter trigger — right side */}
  <button
    onClick={() => setIsOpen(o => !o)}
    style={{
      position: 'relative',
      background:   config.triggerBg          || '#ffffff',
      color:        config.triggerTextColor    || '#111111',
      border:       `1px solid ${config.triggerBorderColor || '#d1d5db'}`,
      borderRadius: config.triggerBorderRadius || '6px',
      padding:      '6px 14px',
      display:      'flex', alignItems: 'center', gap: 6,
      cursor:       'pointer', fontWeight: 600, fontSize: '0.875rem',
    }}
  >
    {(config.showFilterIndicator && filterOn) && (
      <span style={{
        position: 'absolute', top: 3, right: 3,
        width: 8, height: 8, borderRadius: '50%', background: '#3b82f6',
      }} />
    )}
    {(config.triggerShowIcon  ?? true) && <AiOutlineMenuFold size={16} />}
    {(config.triggerShowLabel ?? true) && triggerLabel}
  </button>
</div>

      {/* Backdrop */}
      {isOpen && (
        <div
          onClick={() => setIsOpen(false)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.3)', zIndex: 98 }}
        />
      )}

      {/* Panel */}
      {isOpen && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 99 }}>
          {panelInner}
        </div>
      )}
    </div>
  )
}

export default PanelSearch